/**
 * OaSIS Explorer Frontend - Version Selector Component
 * Task 32.6: Frontend Version Routing
 * 
 * Dropdown component for switching between dataset versions.
 * Shows in navbar on versioned pages.
 */

import React from 'react';
import { Database } from 'lucide-react';
import { useDatasetVersion, useAvailableVersions, type DatasetVersion } from '../hooks/useDatasetVersion';
import { useTranslation } from '../contexts';

interface VersionSelectorProps {
  /** Additional CSS classes */
  className?: string;
  /** Compact mode for mobile */
  compact?: boolean;
}

/**
 * Version selector dropdown for switching between dataset versions
 * 
 * Always renders in navbar so users can select version before navigating
 */
export const VersionSelector: React.FC<VersionSelectorProps> = ({ 
  className = '',
  compact = false,
}) => {
  const t = useTranslation();
  const { version, switchVersion, sourceSummary } = useDatasetVersion();
  const { versions, isLoading } = useAvailableVersions();

  const formatUtcTimestamp = (value: string | null): string => {
    if (!value) {
      return t('version.provenance.notAvailable');
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes} UTC`;
  };

  const snapshotLabel = formatUtcTimestamp(sourceSummary.sourceSnapshotCapturedAt);
  const packageLabel = formatUtcTimestamp(sourceSummary.sourcePackageMetadataModified);
  const provenanceTitle =
    `${t('version.provenance.snapshotCaptured')}: ${snapshotLabel}\n` +
    `${t('version.provenance.packageModified')}: ${packageLabel}`;
  
  // Don't render while loading or if no versions available
  if (isLoading || versions.length === 0) {
    return null;
  }
  
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newVersion = e.target.value as DatasetVersion;
    switchVersion(newVersion);
  };
  
  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      {!compact && (
        <Database size={16} className="hidden text-gray-500 dark:text-gray-400 sm:block" />
      )}
      <select
        value={version}
        onChange={handleChange}
        className={`
          px-2 py-1.5 text-sm font-medium
          bg-white dark:bg-gray-800
          border border-gray-300 dark:border-gray-600
          rounded-lg
          text-gray-700 dark:text-gray-300
          hover:border-gray-400 dark:hover:border-gray-500
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
          transition-colors duration-200
          cursor-pointer
          ${compact ? 'w-full sm:w-20' : 'w-full sm:w-auto'}
          min-w-0 max-w-full
        `}
        aria-label="Select dataset version"
        title={provenanceTitle}
      >
        {versions.map((v) => (
          <option key={v.version} value={v.version}>
            {compact ? v.version : v.name}
          </option>
        ))}
      </select>
      {!compact && (
        <div className="ml-1 hidden text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:block">
          <div>{t('version.provenance.snapshotCaptured')}: {snapshotLabel}</div>
          <div>{t('version.provenance.packageModified')}: {packageLabel}</div>
        </div>
      )}
    </div>
  );
};

/**
 * Version badge for display purposes (non-interactive)
 */
export const VersionBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { version, isVersionedRoute } = useDatasetVersion();
  
  if (!isVersionedRoute) {
    return null;
  }
  
  return (
    <span 
      className={`
        inline-flex items-center gap-1.5 px-2 py-1
        text-xs font-medium
        bg-primary-50 dark:bg-primary-900/30
        text-primary-700 dark:text-primary-300
        rounded-md
        ${className}
      `}
    >
      <Database size={12} />
      {version}
    </span>
  );
};

export default VersionSelector;
